import React from 'react';
import { Box } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { analyticsUiTokens as T } from '../../constants/userAnalytics';

export const ELLIPSIS = '…';

/** All pages when there are 7 or fewer; otherwise 1 … cur-1 cur cur+1 … last. */
export const buildPageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const list = [1];
  if (current > 3) list.push(ELLIPSIS);
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p += 1) list.push(p);
  if (current < total - 2) list.push(ELLIPSIS);
  list.push(total);
  return list;
};

const buttonSx = (active) => ({
  minWidth: 32,
  height: 32,
  px: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: active ? 700 : 600,
  borderRadius: '8px',
  border: `1px solid ${active ? T.navy : T.slate[200]}`,
  bgcolor: active ? T.navy : '#ffffff',
  color: active ? '#ffffff' : T.slate[700],
  cursor: 'pointer',
  transition: 'background-color 0.15s',
  '&:hover:not(:disabled)': { bgcolor: active ? T.navy : T.slate[100] },
  '&:disabled': { opacity: 0.4, cursor: 'default' },
  '&:focus-visible': { outline: `2px solid ${T.navy}`, outlineOffset: 2 },
});

/** Prev / numbered pages with ellipsis / Next. Prev and Next lose their text below `sm`. */
const Pagination = ({ page, totalPages, onChange }) => {
  const prevLabel = useLocalText('Previous page');
  const nextLabel = useLocalText('Next page');
  const pageWord = useLocalText('Page');
  const items = buildPageList(page, totalPages);

  return (
    <Box component="nav" aria-label="Pagination" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <Box component="button" type="button" aria-label={prevLabel} disabled={page <= 1} onClick={() => onChange(page - 1)} sx={buttonSx(false)}>
        <ChevronLeftIcon sx={{ fontSize: 16 }} />
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}><LocalText>Prev</LocalText></Box>
      </Box>

      {items.map((item, i) => (item === ELLIPSIS ? (
        <Box key={`gap-${i}`} component="span" aria-hidden="true" sx={{ px: 1, fontSize: 12, fontWeight: 600, color: T.slate[500] }}>{ELLIPSIS}</Box>
      ) : (
        <Box
          key={item}
          component="button"
          type="button"
          aria-label={`${pageWord} ${item}`}
          aria-current={item === page ? 'page' : undefined}
          onClick={() => onChange(item)}
          sx={buttonSx(item === page)}
        >
          {item}
        </Box>
      )))}

      <Box component="button" type="button" aria-label={nextLabel} disabled={page >= totalPages} onClick={() => onChange(page + 1)} sx={buttonSx(false)}>
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}><LocalText>Next</LocalText></Box>
        <ChevronRightIcon sx={{ fontSize: 16 }} />
      </Box>
    </Box>
  );
};

export default Pagination;
